import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { format } from "date-fns";
import { Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { DatePicker, ConfigProvider, theme as antTheme } from "antd";
import dayjs from "dayjs";
import { formatDisplayDate } from "../utils/dateFormat";
import { Calendar } from "lucide-react";
import { toast } from "react-toastify";
import { useDistrict } from "../utils/DistrictContext";
import { SwapOutlined } from "@ant-design/icons";
import { formatUSD, formatNumberUS } from "../utils/currencyUtils";
import {
  getWaterRate,
  addWaterRate,
  deleteWaterRate,
  editWaterRate,
  getMaintenanceLogs,
  addMaintenanceLog,
  deleteMaintenanceLog,
  updateMaintenanceLog,
} from "../services";
import moment from "moment";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function LeakRate() {
  const { theme, colors } = useTheme();
  const { selectedDistrict } = useDistrict();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [usageEntriesState, setUsageEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showMaintenanceDeleteModal, setShowMaintenanceDeleteModal] =
    useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [maintenanceTotalFlow, setMaintenanceTotalFlow] = useState(0);

  const [waterPage, setWaterPage] = useState(1);
  const [waterPageSize, setWaterPageSize] = useState(5);
  const [waterTotalPages, setWaterTotalPages] = useState(1);
  const [waterTotalRecords, setWaterTotalRecords] = useState(0);

  const [maintenancePage, setMaintenancePage] = useState(1);
  const [maintenancePageSize, setMaintenancePageSize] = useState(5);
  const [maintenanceTotalPages, setMaintenanceTotalPages] = useState(1);
  const [maintenanceTotalRecords, setMaintenanceTotalRecords] = useState(0);

  const [rangeOption, setRangeOption] = useState("Past Day");
  const [intervalOption, setIntervalOption] = useState("Day");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterParams, setFilterParams] = useState({});

  const { RangePicker } = DatePicker;

  const handleDateChange = (dates, strings) => {
    setStartDate(strings[0]);
    setEndDate(strings[1]);
  };

  const disabledDate = (current) => {
    if (!current) return false;

    const usToday = dayjs().tz("America/New_York").format("YYYY-MM-DD");
    const currentDate = current.format("YYYY-MM-DD");

    return currentDate > usToday;
  };

  const [sortConfigMaintenance, setSortConfigMaintenance] = useState({
    key: null,
    direction: "asc",
  });

  const [sortConfigLeakRate, setSortConfigLeakRate] = useState({
    key: null,
    direction: "asc",
  });

  const handleSortMaintenance = (key) => {
    let direction = "asc";
    if (
      sortConfigMaintenance.key === key &&
      sortConfigMaintenance.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfigMaintenance({ key, direction });
  };

  const handleSortLeakRate = (key) => {
    let direction = "asc";
    if (
      sortConfigLeakRate.key === key &&
      sortConfigLeakRate.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfigLeakRate({ key, direction });
  };

  const sortedMaintenanceLogs = useMemo(() => {
    let data = [...maintenanceLogs];

    if (sortConfigMaintenance.key) {
      data.sort((a, b) => {
        const aValue = a[sortConfigMaintenance.key];
        const bValue = b[sortConfigMaintenance.key];
        if (aValue instanceof Date && bValue instanceof Date) {
          return sortConfigMaintenance.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfigMaintenance.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }
        return sortConfigMaintenance.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }
    return data;
  }, [maintenanceLogs, sortConfigMaintenance]);

  const sortedLeakRates = useMemo(() => {
    let data = [...usageEntriesState];
    if (sortConfigLeakRate.key) {
      data.sort((a, b) => {
        const aValue = a[sortConfigLeakRate.key];
        const bValue = b[sortConfigLeakRate.key];
        if (aValue instanceof Date && bValue instanceof Date) {
          return sortConfigLeakRate.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfigLeakRate.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }
        return sortConfigLeakRate.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }

    return data;
  }, [usageEntriesState, sortConfigLeakRate]);

  const [maintenanceForm, setMaintenanceForm] = useState({
    id: null,
    volume: "",
    category: "",
    location: "",
    date: null,
  });

  const [editFormData, setEditFormData] = useState({
    id: null,
    volume: "",
    amount: "",
    startDate: null,
    endDate: null,
  });

  const handleEditClick = (entry) => {
    setEditFormData({
      id: entry.id,
      volume: entry.volume,
      amount: entry.amount,
      startDate: entry.startDate,
      endDate: entry.endDate,
      isDefault: entry.isDefault,
    });

    setShowEditModal(true);
  };

  const handleCancelEditModal = () => {
    setShowEditModal(false);
  };

  const handleUpdateUsageEntry = async () => {
    try {
      if (
        !editFormData.volume ||
        !editFormData.amount ||
        (!editFormData.isDefault &&
          (!editFormData.startDate || !editFormData.endDate))
      ) {
        toast.error("Please fill all fields");
        return;
      }
      if (
        !editFormData.isDefault &&
        isEndDateBeforeStartDate(editFormData.startDate, editFormData.endDate)
      ) {
        toast.error("End date cannot be earlier than start date");
        return;
      }

      // const payload = {
      //   volume_gallons: Number(editFormData.volume),
      //   amount: Number(editFormData.amount),
      //   start_date: dayjs(editFormData.startDate).format("YYYY-MM-DD"),
      //   end_date: dayjs(editFormData.endDate).format("YYYY-MM-DD"),
      // };
      const payload = {
        volume_gallons: Number(editFormData.volume),
        amount: Number(editFormData.amount),
      };

      if (!editFormData.isDefault) {
        payload.start_date = dayjs(editFormData.startDate).format("YYYY-MM-DD");
        payload.end_date = dayjs(editFormData.endDate).format("YYYY-MM-DD");
      }

      const res = await editWaterRate(editFormData.id, payload);

      if (res?.status) {
        await fetchWaterRates();

        setShowEditModal(false);
        toast.success("Leak rate updated successfully");
      }
    } catch (error) {
      console.error("Edit failed:", error);
    }
  };

  const handleDeleteClick = (entry) => {
    setSelectedEntry(entry);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await deleteWaterRate(selectedEntry.id);

      if (res?.status) {
        await fetchWaterRates();

        setShowDeleteConfirmation(false);
        toast.success("Leak rate deleted successfully");
      }
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const isEndDateBeforeStartDate = (start, end) => {
    if (!start || !end) return false;
    return dayjs(end).isBefore(dayjs(start), "day");
  };

  const disableEditEndDate = (current) => {
    if (!editFormData.startDate) return false;
    const start = dayjs(editFormData.startDate).startOf("day");
    return current && current.isBefore(start);
  };

  const fetchWaterRates = async () => {
    try {
      setLoading(true);

      const res = await getWaterRate(waterPage, waterPageSize);

      if (res?.status) {
        const defaultRow = res.default
          ? {
              id: res.default.id,
              volume: res.default.volume_gallons,
              amount: res.default.amount,
              startDate: null,
              endDate: null,
              isDefault: true,
            }
          : null;

        const formattedData = res.data.map((item) => ({
          id: item.id,
          volume: item.volume_gallons,
          amount: item.amount,
          startDate: item.start_date
            ? new Date(item.start_date.split("-").reverse().join("-"))
            : null,
          endDate: item.end_date
            ? new Date(item.end_date.split("-").reverse().join("-"))
            : null,
          isEditable: true,
        }));

        const finalData = defaultRow
          ? [defaultRow, ...formattedData]
          : formattedData;

        // setUsageEntries(formattedData);
        setUsageEntries(finalData);
        setWaterTotalPages(res.pagination.total_pages);
        setWaterTotalRecords(res.pagination.total_records);
      }
    } catch (error) {
      console.error("Water rate fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceLogs = async () => {
    try {
      const res = await getMaintenanceLogs(
        maintenancePage,
        maintenancePageSize,
        filterParams,
      );

      if (res?.data?.status) {
        const formatted = res.data.records.map((item) => ({
          id: item.id,
          volume: item.flow_value, // rename later in UI
          category: item.type === "Maintanence" ? "Maintenance" : item.type,
          location: item.location || "-", // NEW
          date: item.date ? new Date(item.date) : null, // SINGLE DATE
        }));

        setMaintenanceLogs(formatted);
        setMaintenanceTotalPages(res.data.total_pages);
        setMaintenanceTotalRecords(res.data.total_records);
        setMaintenanceTotalFlow(res.data.total_flow);
      }
    } catch (error) {
      console.error("Maintenance logs fetch failed:", error);
      toast.error("Failed to load maintenance logs");
    }
  };

  const handleAddMaintenance = async () => {
    try {
      if (
        !maintenanceForm.volume ||
        !maintenanceForm.category ||
        !maintenanceForm.date
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      const payload = {
        date: dayjs(maintenanceForm.date).format("YYYY-MM-DD"),
        type: maintenanceForm.category,
        flow_value: Number(maintenanceForm.volume),
        location: maintenanceForm.location || "",
      };

      const res = await addMaintenanceLog(payload);

      if (res?.status || res?.data?.status) {
        toast.success("Maintenance log added successfully");

        setShowMaintenanceModal(false);
        await fetchMaintenanceLogs();

        setMaintenanceForm({
          id: null,
          volume: "",
          category: "",
          location: "",
          date: null,
        });
      } else {
        toast.error(res?.data?.message || "Failed to add maintenance log");
      }
    } catch (error) {
      console.error("Add maintenance failed:", error);

      toast.error(
        error?.response?.data?.data?.message || "Something went wrong",
      );
    }
  };

  const confirmDeleteMaintenance = async () => {
    try {
      const res = await deleteMaintenanceLog(selectedMaintenance.id);

      if (res?.status || res?.data?.status) {
        toast.success("Maintenance log deleted successfully");

        setShowMaintenanceDeleteModal(false);
        setSelectedMaintenance(null);

        await fetchMaintenanceLogs();
      }
    } catch (error) {
      console.error("Delete failed:", error);

      toast.error(
        error?.response?.data?.message || "Failed to delete maintenance log",
      );
    }
  };

  const handleUpdateMaintenance = async () => {
    try {
      if (
        !maintenanceForm.volume ||
        !maintenanceForm.category ||
        !maintenanceForm.date
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      const payload = {
        date: dayjs(maintenanceForm.date).format("YYYY-MM-DD"),
        type: maintenanceForm.category,
        flow_value: Number(maintenanceForm.volume),
        location: maintenanceForm.location || "",
      };

      const res = await updateMaintenanceLog(maintenanceForm.id, payload);

      if (res?.status || res?.data?.status) {
        toast.success("Maintenance log updated successfully");

        setShowMaintenanceModal(false);
        await fetchMaintenanceLogs();

        setMaintenanceForm({
          id: null,
          volume: "",
          category: "",
          location: "",
          date: null,
        });
      }
    } catch (error) {
      console.error("Update failed:", error);

      toast.error(
        error?.response?.data?.message || "Failed to update maintenance log",
      );
    }
  };

  useEffect(() => {
    fetchWaterRates();
  }, [waterPage, waterPageSize, selectedDistrict]);

  useEffect(() => {
    fetchMaintenanceLogs();
  }, [maintenancePage, maintenancePageSize, selectedDistrict, filterParams]);

  if (loading) {
    return (
      <div style={{ color: colors.textSecondary }}>Loading leak rates...</div>
    );
  }

  const handleAddFromModal = async () => {
    try {
      if (
        !editFormData.volume ||
        !editFormData.amount ||
        (!editFormData.isDefault &&
          (!editFormData.startDate || !editFormData.endDate))
      ) {
        toast.error("Please fill all fields");
        return;
      }

      if (
        isEndDateBeforeStartDate(editFormData.startDate, editFormData.endDate)
      ) {
        toast.error("End date cannot be earlier than start date");
        return;
      }

      const payload = {
        volume_gallons: Number(editFormData.volume),
        amount: Number(editFormData.amount),
      };

      if (!editFormData.isDefault) {
        payload.start_date = dayjs(editFormData.startDate).format("YYYY-MM-DD");
        payload.end_date = dayjs(editFormData.endDate).format("YYYY-MM-DD");
      }
      const res = await addWaterRate(payload);

      if (res?.status) {
        await fetchWaterRates();
        setShowAddModal(false);
        toast.success("Leak rate added successfully");
      }
    } catch (error) {
      console.error("Add failed:", error);
      toast.error(error?.response?.data?.message || "Something went wrong");

      setShowAddModal(false);
    }
  };

  const isActionAllowed = (endDate) => {
    if (!endDate) return false;
    const today = dayjs().startOf("day");
    const end = dayjs(endDate).startOf("day");
    return end.isSame(today) || end.isAfter(today);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Maintenance Log */}
      <div
        className="backdrop-blur-sm rounded-xl border overflow-hidden"
        style={{
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
        }}
      >
        <div
          className="p-6 border-b flex items-center justify-between flex-wrap gap-4"
          style={{ borderColor: colors.border }}
        >
          <h2
            className="text-xl font-bold text-transparent bg-clip-text"
            style={{
              backgroundImage: colors.gradient,
              WebkitTextFillColor: "transparent",
            }}
          >
            Maintenance Log
          </h2>
          {/*  */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1">
                <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Range | Interval | Custom
                </label>
                <div
                  className="flex items-center gap-1 p-1 rounded-xl border w-full justify-between flex-wrap"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                  }}
                >
                  {/* --- SECTION 1: RANGE PRESETS --- */}
                  <div className="flex items-center gap-1 flex-wrap">
                    {[
                      { label: "D", value: "past_day" },
                      { label: "W", value: "past_week" },
                      { label: "1M", value: "past_month" },
                      { label: "3M", value: "past_3_month" },
                      { label: "YTD", value: "current_year" },
                      { label: "1Y", value: "last_1_year" },
                    ].map((tp) => {
                      const isYTD =
                        tp.value === "YTD" &&
                        startDate ===
                          moment().startOf("year").format("YYYY-MM-DD");

                      const isActive = rangeOption === tp.value || isYTD; // ✅ FIX

                      return (
                        <button
                          key={tp.value}
                          onClick={() => {
                            if (tp.value === "current_year") {
                              const startOfYear = moment()
                                .startOf("year")
                                .format("YYYY-MM-DD");
                              const today = moment().format("YYYY-MM-DD");

                              setStartDate(startOfYear);
                              setEndDate(today);

                              setFilterParams({
                                option: "custom",
                                start_date: startOfYear,
                                end_date: today,
                              });

                              setRangeOption("Custom");
                            } else {
                              setRangeOption(tp.value);

                              setFilterParams({
                                option: tp.value, // 🔥 THIS IS THE MAIN THING
                              });

                              setStartDate(null);
                              setEndDate(null);
                            }

                            setMaintenancePage(1); // reset page
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold"
                          style={{
                            backgroundColor: isActive
                              ? `${colors.primary}20`
                              : "transparent",
                            color: isActive
                              ? colors.primary
                              : colors.textSecondary,
                            border: isActive
                              ? `1px solid ${colors.primary}40`
                              : "1px solid transparent",
                          }}
                        >
                          {tp.label}
                        </button>
                      );
                    })}
                  </div>
                  <div
                    className="h-6 w-[1px] mx-1"
                    style={{ backgroundColor: colors.border }}
                  />

                  {/* --- SECTION 3: CALENDAR --- */}
                  <ConfigProvider
                    theme={{
                      algorithm:
                        theme === "white"
                          ? antTheme.defaultAlgorithm
                          : antTheme.darkAlgorithm,
                      token: {
                        colorPrimary: colors.primary,
                        controlHeight: 32,
                        borderRadius: 8,
                      },
                    }}
                  >
                    <RangePicker
                      value={[
                        startDate ? dayjs(startDate, "YYYY-MM-DD") : null,
                        endDate ? dayjs(endDate, "YYYY-MM-DD") : null,
                      ]}
                      onChange={(dates, strings) => {
                        handleDateChange(dates, strings);

                        setFilterParams({
                          option: "custom",
                          start_date: strings[0],
                          end_date: strings[1],
                        });

                        setRangeOption("Custom");
                        setMaintenancePage(1);
                      }}
                      disabledDate={disabledDate}
                      placeholder={["Start", "End"]}
                      variant="borderless"
                      separator={
                        <SwapOutlined style={{ color: colors.textSecondary }} />
                      }
                      className="custom-range-picker cursor-pointer"
                      style={{ width: "240px" }}
                      popupClassName={`mizu-calendar-popup calendar-${theme}`}
                    />
                  </ConfigProvider>
                </div>
              </div>
              <div className="">
                <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Total Flow
                </label>
                <div
                  className="flex items-center gap-1 p-1 rounded-xl border justify-between"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    width: "fit-content",
                  }}
                >
                  <h5
                    style={{ color: colors.textSecondary }}
                    className="px-4 py-1"
                  >
                    {" "}
                    {formatNumberUS(maintenanceTotalFlow)}
                  </h5>
                </div>
              </div>
            </div>

            {/*  */}

            <button
              onClick={() => {
                setMaintenanceForm({
                  id: null,
                  volume: "",
                  category: "",
                  location: "",
                  date: null,
                });
                setShowMaintenanceModal(true);
              }}
              className="px-4 py-2 rounded-lg text-white font-semibold"
              style={{ backgroundImage: colors.gradient }}
            >
              + Add Maintenance
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr
                className="border-b"
                style={{
                  borderColor: colors.border,
                  backgroundColor: `${colors.background}80`,
                }}
              >
                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("date")}
                >
                  <span className="flex items-center gap-2">
                    Date
                    {sortConfigMaintenance.key === "date" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("volume")}
                >
                  <span className="flex items-center gap-2">
                    Water Usage (Gallons)
                    {sortConfigMaintenance.key === "volume" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("location")}
                >
                  <span className="flex items-center gap-2">
                    Location
                    {sortConfigMaintenance.key === "location" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("category")}
                >
                  <span className="flex items-center gap-2">
                    Category
                    {sortConfigMaintenance.key === "category" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="px-6 py-4 text-center text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {maintenanceLogs.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center py-6"
                    style={{ color: colors.textSecondary }}
                  >
                    No maintenance logs found
                  </td>
                </tr>
              ) : (
                sortedMaintenanceLogs.map((item, index) => (
                  <motion.tr
                    key={item.id}
                    className="border-b"
                    style={{ borderColor: colors.border }}
                  >
                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.date ? formatDisplayDate(item.date) : "-"}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {/* {item.volume} */}
                      {formatNumberUS(item.volume)}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.location}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.category}
                    </td>

                    <td className="px-6 py-4 text-center flex justify-center gap-5">
                      <button
                        onClick={() => {
                          setMaintenanceForm({
                            id: item.id,
                            volume: item.volume,
                            category: item.category,
                            location: item.location,
                            date: item.date,
                          });

                          setShowMaintenanceModal(true);
                        }}
                      >
                        <Pencil className="w-4 h-4 text-blue-400" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMaintenance(item);
                          setShowMaintenanceDeleteModal(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
          <div
            className="flex items-center justify-between p-4 border-t"
            // style={{
            //   borderColor: colors.border,
            //   backgroundColor: colors.cardBg,
            // }}
            style={{ color: colors.textSecondary }}
          >
            {/* LEFT TEXT */}
            <div className="text-xs" style={{ color: colors.textSecondary }}>
              Showing {(maintenancePage - 1) * maintenancePageSize + 1} to{" "}
              {Math.min(
                maintenancePage * maintenancePageSize,
                maintenanceTotalRecords,
              )}{" "}
              of {maintenanceTotalRecords} records
            </div>

            <div className="flex items-center gap-4">
              <button
                disabled={maintenancePage === 1}
                onClick={() => setMaintenancePage((p) => Math.max(p - 1, 1))}
              >
                Prev
              </button>

              <select
                value={maintenancePage}
                onChange={(e) => setMaintenancePage(Number(e.target.value))}
              >
                {Array.from(
                  { length: maintenanceTotalPages },
                  (_, i) => i + 1,
                ).map((page) => (
                  <option key={page} value={page}>
                    {page}
                  </option>
                ))}
              </select>

              {/* NEXT */}
              <button
                disabled={maintenancePage === maintenanceTotalPages}
                onClick={() =>
                  setMaintenancePage((p) =>
                    Math.min(p + 1, maintenanceTotalPages),
                  )
                }
              >
                Next
              </button>
              {/* PAGE SIZE */}
              <select
                value={maintenancePageSize}
                onChange={(e) => {
                  setMaintenancePageSize(Number(e.target.value));
                  setMaintenancePage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Leak Rate Schedule */}
      <div
        className="backdrop-blur-sm rounded-xl border overflow-hidden mb-5"
        style={{
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
        }}
      >
        <div
          className="p-6 border-b flex items-center justify-between"
          style={{ borderColor: colors.border }}
        >
          <h2
            className="text-xl font-bold text-transparent bg-clip-text"
            style={{
              backgroundImage: colors.gradient,
              WebkitTextFillColor: "transparent",
              WebkitBackgroundClip: "text",
            }}
          >
            Water Rate - Schedule
          </h2>

          <button
            onClick={() => {
              setEditFormData({
                id: null,
                volume: "1000",
                amount: "",
                startDate: null,
                endDate: null,
                isDefault: false,
              });
              setShowAddModal(true);
            }}
            className="px-4 py-2 rounded-lg text-white font-semibold"
            style={{ backgroundImage: colors.gradient }}
          >
            + Add Water Rate
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr
                className="border-b"
                style={{
                  borderColor: colors.border,
                  backgroundColor: `${colors.background}80`,
                }}
              >
                <th
                  className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortLeakRate("volume")}
                >
                  <span className="flex items-center gap-2">
                    Volume (Gallons)
                    {sortConfigLeakRate.key === "volume" ? (
                      sortConfigLeakRate.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortLeakRate("amount")}
                >
                  <span className="flex items-center gap-2">
                    Amount
                    {sortConfigLeakRate.key === "amount" ? (
                      sortConfigLeakRate.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortLeakRate("startDate")}
                >
                  <span className="flex items-center gap-2">
                    Start Date
                    {sortConfigLeakRate.key === "startDate" ? (
                      sortConfigLeakRate.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortLeakRate("endDate")}
                >
                  <span className="flex items-center gap-2">
                    End Date
                    {sortConfigLeakRate.key === "endDate" ? (
                      sortConfigLeakRate.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="text-center py-4 px-6 font-semibold text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {usageEntriesState.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-6"
                    style={{ color: colors.textSecondary }}
                  >
                    No leak rate records found
                  </td>
                </tr>
              ) : (
                sortedLeakRates.map((entry, index) => (
                  <motion.tr
                    key={entry.id}
                    className="border-b transition-colors"
                    style={{ borderColor: colors.border }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = `${colors.primary}0D`)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    {/* <td
                      className="py-4 px-6 text-sm"
                      style={{ color: colors.text }}
                    >
                      {formatNumberUS(entry.volume)}
                    </td> */}
                    <td
                      className="py-4 px-6 text-sm"
                      style={{ color: colors.text }}
                    >
                      <div className="flex items-center gap-2">
                        {formatNumberUS(entry.volume)}

                        {entry.isDefault && (
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                            style={{
                              backgroundColor: `${colors.primary}25`,
                              color: colors.primary,
                              border: `1px solid ${colors.primary}40`,
                            }}
                          >
                            Default
                          </span>
                        )}
                      </div>
                    </td>

                    <td
                      className="py-4 px-6 text-sm"
                      style={{ color: colors.text }}
                    >
                      {/* ${entry.amount} */}
                      {formatUSD(entry.amount)}
                    </td>

                    <td
                      className="py-4 px-6 text-sm"
                      style={{ color: colors.text }}
                    >
                      {entry.startDate
                        ? formatDisplayDate(entry.startDate)
                        : "N/A"}
                    </td>

                    <td
                      className="py-4 px-6 text-sm"
                      style={{ color: colors.text }}
                    >
                      {entry.endDate ? formatDisplayDate(entry.endDate) : "N/A"}
                    </td>

                    {/* <td className="py-4 px-6">
                      {entry.isEditable && isActionAllowed(entry.endDate) && (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEditClick(entry)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors group"
                          >
                            <Pencil
                              className="w-4 h-4 group-hover:scale-110 transition-transform"
                              style={{ color: colors.primary }}
                            />
                          </button>

                          <button
                            onClick={() => handleDeleteClick(entry)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-red-500/10 transition-colors group"
                          >
                            <Trash2 className="w-4 h-4 text-red-400 group-hover:scale-110 transition-transform" />
                          </button>
                        </div>
                      )}
                    </td> */}
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEditClick(entry)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors group"
                        >
                          <Pencil
                            className="w-4 h-4 group-hover:scale-110 transition-transform"
                            style={{ color: colors.primary }}
                          />
                        </button>

                        {!entry.isDefault && (
                          <button
                            onClick={() => handleDeleteClick(entry)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-red-500/10 transition-colors group"
                          >
                            <Trash2 className="w-4 h-4 text-red-400 group-hover:scale-110 transition-transform" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
          <div
            className="flex items-center justify-between p-4 border-t"
            style={{ color: colors.textSecondary }}
          >
            {/* LEFT TEXT */}
            <div className="text-xs">
              Showing {(waterPage - 1) * waterPageSize + 1} to{" "}
              {Math.min(waterPage * waterPageSize, waterTotalRecords)} of{" "}
              {waterTotalRecords} records
            </div>

            <div className="flex items-center gap-4">
              {/* PREV */}
              <button
                disabled={waterPage === 1}
                onClick={() => setWaterPage((p) => Math.max(p - 1, 1))}
              >
                Prev
              </button>

              {/* PAGE SELECT */}
              <select
                value={waterPage}
                onChange={(e) => setWaterPage(Number(e.target.value))}
              >
                {Array.from({ length: waterTotalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <option key={page} value={page}>
                      {page}
                    </option>
                  ),
                )}
              </select>

              {/* NEXT */}
              <button
                disabled={waterPage === waterTotalPages}
                onClick={() =>
                  setWaterPage((p) => Math.min(p + 1, waterTotalPages))
                }
              >
                Next
              </button>

              {/* PAGE SIZE */}
              <select
                value={waterPageSize}
                onChange={(e) => {
                  setWaterPageSize(Number(e.target.value));
                  setWaterPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {(showEditModal || showAddModal) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          style={{ backgroundColor: colors.modalOverlay }}
        >
          <motion.div
            className="w-full max-w-lg backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border"
            style={{
              background: colors.inputBg, // Adding opacity to the card background
              borderColor: colors.border,
            }}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div
              className="p-6 border-b flex justify-between items-center"
              style={{ borderColor: colors.border }}
            >
              <h2
                className="text-xl font-bold text-transparent bg-clip-text"
                style={{
                  backgroundImage: colors.gradient,
                  WebkitTextFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                }}
              >
                {showAddModal ? "Add Water Rate" : "Edit Water Rate"}
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setShowAddModal(false);
                }}
                className="ml-4 text-lg font-bold hover:scale-110 transition"
                style={{ color: colors.textSecondary }}
              >
                ✕
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              {/* Volume */}
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Volume(Gallons)
                </label>

                <div
                  className="flex items-center rounded-lg border overflow-hidden"
                  style={{
                    backgroundColor: `${colors.background}80`,
                    borderColor: colors.border,
                  }}
                >
                  {/* <input
                    type="text"
                    value={editFormData.volume}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        volume: e.target.value,
                      })
                    }
                    className="flex-1 px-4 py-2.5 bg-transparent outline-none"
                    style={{ color: colors.text }}
                  /> */}
                  <input
                    type="text"
                    value={editFormData.volume}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (/^\d{0,4}$/.test(value)) {
                        setEditFormData({
                          ...editFormData,
                          volume: value,
                        });
                      }
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="flex-1 px-4 py-2.5 bg-transparent outline-none"
                    style={{ color: colors.text }}
                  />

                  <span
                    className="pr-4 text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Gallons
                  </span>
                </div>
              </div>

              {/* Amount */}
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Amount
                </label>

                <div className="relative">
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    $
                  </span>

                  {/* <input
                    type="text"
                    value={editFormData.amount}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        amount: e.target.value,
                      })
                    }
                    className="w-full pl-8 pr-4 py-2.5 rounded-lg border outline-none"
                    style={{
                      backgroundColor: `${colors.background}80`,
                      borderColor: colors.border,
                      color: colors.text,
                    }}
                  /> */}
                  <input
                    type="text"
                    value={editFormData.amount}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (/^\d{0,4}(\.\d{0,2})?$/.test(value)) {
                        setEditFormData({
                          ...editFormData,
                          amount: value,
                        });
                      }
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="w-full pl-8 pr-4 py-2.5 rounded-lg border outline-none"
                    style={{
                      backgroundColor: `${colors.background}80`,
                      borderColor: colors.border,
                      color: colors.text,
                    }}
                  />
                </div>
              </div>

              {!editFormData.isDefault && (
                <div className="flex flex-col gap-2">
                  <label
                    className="text-xs font-medium"
                    style={{ color: colors.textSecondary }}
                  >
                    Date
                  </label>

                  <div className="flex items-center gap-3">
                    {/* Start Date */}
                    <div className="flex-1">
                      <div
                        className="border rounded-lg px-2 py-1"
                        style={{
                          background: colors.cardBg,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <ConfigProvider
                          theme={{
                            token: {
                              colorPrimary: colors.primary,
                              colorText: colors.text,
                              colorTextSecondary: colors.textSecondary,
                              colorBorder: colors.border,
                              colorBgContainer: colors.background,
                            },
                            components: {
                              DatePicker: {
                                colorBgContainer: colors.background,
                                colorBgElevated: colors.cardBg,
                                colorText: colors.text,
                                colorTextHeading: colors.text,
                                colorTextDisabled: colors.textSecondary,
                                cellHoverBg: colors.primary + "20",
                                cellActiveWithRangeBg: colors.primary + "30",
                                cellRangeBorderColor: colors.primary,
                                activeBorderColor: colors.primary,
                                hoverBorderColor: colors.primary,
                              },
                            },
                          }}
                        >
                          <DatePicker
                            picker="date"
                            value={
                              editFormData.startDate
                                ? dayjs(editFormData.startDate)
                                : null
                            }
                            onChange={(date) => {
                              if (!date) return;

                              const newStartDate = date.toDate();

                              setEditFormData((prev) => ({
                                ...prev,
                                startDate: newStartDate,
                                endDate:
                                  prev.endDate &&
                                  dayjs(prev.endDate).isBefore(
                                    dayjs(newStartDate),
                                    "day",
                                  )
                                    ? null
                                    : prev.endDate,
                              }));
                            }}
                            placeholder="Start date"
                            allowClear={false}
                            suffixIcon={
                              <Calendar
                                size={16}
                                style={{ color: colors.textSecondary }}
                              />
                            }
                            className="w-full"
                          />
                        </ConfigProvider>
                      </div>
                    </div>

                    {/* End Date */}
                    <div className="flex-1">
                      <div
                        className="border rounded-lg px-2 py-1"
                        style={{
                          background: colors.cardBg,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <ConfigProvider
                          theme={{
                            token: {
                              colorPrimary: colors.primary,
                              colorText: colors.text,
                              colorTextSecondary: colors.textSecondary,
                              colorBorder: colors.border,
                              colorBgContainer: colors.background,
                            },
                            components: {
                              DatePicker: {
                                colorBgContainer: colors.background,
                                colorBgElevated: colors.cardBg,
                                colorText: colors.text,
                                colorTextHeading: colors.text,
                                colorTextDisabled: colors.textSecondary,
                                cellHoverBg: colors.primary + "20",
                                cellActiveWithRangeBg: colors.primary + "30",
                                cellRangeBorderColor: colors.primary,
                                activeBorderColor: colors.primary,
                                hoverBorderColor: colors.primary,
                              },
                            },
                          }}
                        >
                          <DatePicker
                            picker="date"
                            value={
                              editFormData.endDate
                                ? dayjs(editFormData.endDate)
                                : null
                            }
                            onChange={(date) => {
                              if (!date) return;
                              setEditFormData({
                                ...editFormData,
                                endDate: date.toDate(),
                              });
                            }}
                            disabledDate={disableEditEndDate}
                            placeholder="End date"
                            allowClear={false}
                            suffixIcon={
                              <Calendar
                                size={16}
                                style={{ color: colors.textSecondary }}
                              />
                            }
                            className="w-full"
                          />
                        </ConfigProvider>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="p-6 border-t flex justify-end gap-3"
              style={{ borderColor: colors.border }}
            >
              <button
                className="px-6 py-2.5 rounded-lg font-semibold"
                style={{
                  backgroundColor: colors.border,
                  color: colors.text,
                }}
                onClick={() => {
                  setShowEditModal(false);
                  setShowAddModal(false);
                }}
              >
                Cancel
              </button>

              <button
                className="px-6 py-2.5 rounded-lg text-white font-semibold"
                style={{ backgroundImage: colors.gradient }}
                onClick={
                  showAddModal ? handleAddFromModal : handleUpdateUsageEntry
                }
              >
                {showAddModal ? "Add" : "Update"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showMaintenanceModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          style={{ backgroundColor: colors.modalOverlay }}
        >
          <motion.div
            className="w-full max-w-lg backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border"
            style={{
              background: colors.inputBg, // Adding opacity to the card background
              borderColor: colors.border,
            }}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div
              className="p-6 border-b flex justify-between items-center"
              style={{ borderColor: colors.border }}
            >
              <h2
                className="text-xl font-bold text-transparent bg-clip-text"
                style={{
                  backgroundImage: colors.gradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {maintenanceForm.id
                  ? "Edit Maintenance Log"
                  : "Add Maintenance Log"}
              </h2>
              <button
                onClick={() => {
                  setShowMaintenanceModal(false);
                  setMaintenanceForm({
                    id: null,
                    volume: "",
                    category: "",
                    location: "",
                    date: null,
                  });
                }}
                className="text-xl transition"
                style={{ color: colors.textSecondary }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = colors.text)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = colors.textSecondary)
                }
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex flex-col gap-5">
              {/* Volume */}
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Volume(Gallons)
                </label>
                <input
                  type="text"
                  value={maintenanceForm.volume}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,6}$/.test(value)) {
                      setMaintenanceForm({
                        ...maintenanceForm,
                        volume: value,
                      });
                    }
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="px-4 py-2.5 rounded-lg border outline-none"
                  style={{
                    backgroundColor: `${colors.background}80`,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                />
              </div>

              {/* Category */}
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Category
                </label>
                <div className="flex gap-4">
                  {["Maintenance", "Leak    "].map((type) => (
                    <label
                      key={type}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={maintenanceForm.category === type}
                        onChange={() => {
                          setMaintenanceForm({
                            ...maintenanceForm,
                            category:
                              maintenanceForm.category === type ? "" : type,
                          });
                        }}
                      />
                      <span style={{ color: colors.text }}>{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Location
                </label>
                <input
                  value={maintenanceForm.location}
                  onChange={(e) =>
                    setMaintenanceForm({
                      ...maintenanceForm,
                      location: e.target.value,
                    })
                  }
                  className="px-4 py-2.5 rounded-lg border outline-none"
                  style={{
                    backgroundColor: `${colors.background}80`,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label
                  className="text-xs font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Date
                </label>

                <div className="flex items-center gap-3">
                  {/* FROM DATE */}
                  <div className="flex-1">
                    <div
                      className="border rounded-lg px-0 py-0"
                      style={{
                        background: colors.cardBg,
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      <ConfigProvider
                        theme={{
                          token: {
                            colorPrimary: colors.primary,
                            colorText: colors.text,
                            colorTextSecondary: colors.textSecondary,
                            colorBorder: colors.border,
                            colorBgContainer: colors.background,
                          },
                          components: {
                            DatePicker: {
                              colorBgContainer: colors.background,
                              colorBgElevated: colors.cardBg,
                              colorText: colors.text,
                              colorTextHeading: colors.text,
                              colorTextDisabled: colors.textSecondary,
                              cellHoverBg: colors.primary + "20",
                              cellActiveWithRangeBg: colors.primary + "30",
                              cellRangeBorderColor: colors.primary,
                              activeBorderColor: colors.primary,
                              hoverBorderColor: colors.primary,
                            },
                          },
                        }}
                      >
                        <DatePicker
                          picker="date"
                          className="w-full rounded-lg"
                          style={{ padding: "8px 12px" }}
                          value={
                            maintenanceForm.date
                              ? dayjs(maintenanceForm.date)
                              : null
                          }
                          disabledDate={disabledDate}
                          onChange={(date) =>
                            setMaintenanceForm({
                              ...maintenanceForm,
                              date: date?.toDate(),
                            })
                          }
                        />
                      </ConfigProvider>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              className="p-6 border-t flex justify-end gap-3"
              style={{ borderColor: colors.border }}
            >
              <button
                className="px-6 py-2.5 rounded-lg font-semibold"
                style={{
                  backgroundColor: colors.border,
                  color: colors.text,
                }}
                onClick={() => setShowMaintenanceModal(false)}
              >
                Cancel
              </button>

              <button
                className="px-6 py-2.5 rounded-lg text-white font-semibold"
                style={{ backgroundImage: colors.gradient }}
                onClick={
                  maintenanceForm.id
                    ? handleUpdateMaintenance
                    : handleAddMaintenance
                }
              >
                {maintenanceForm.id ? "Update" : "Add"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showMaintenanceDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="backdrop-blur-sm rounded-xl border overflow-hidden w-full max-w-md"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            <div className="p-6">
              <h3
                className="text-lg font-semibold mb-4"
                style={{ color: colors.text }}
              >
                Are you sure you want to delete this maintenance log?
              </h3>

              <div className="flex justify-end gap-3">
                {/* Cancel */}
                <button
                  className="px-6 py-2.5 rounded-lg font-semibold"
                  style={{
                    backgroundColor: colors.border,
                    color: colors.text,
                  }}
                  onClick={() => setShowMaintenanceDeleteModal(false)}
                >
                  Cancel
                </button>

                {/* Confirm Delete */}
                <button
                  className="px-6 py-2.5 rounded-lg text-white font-semibold"
                  style={{
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  }}
                  onClick={confirmDeleteMaintenance}
                >
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteConfirmation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="backdrop-blur-sm rounded-xl border overflow-hidden w-full max-w-md"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            <div className="p-6">
              <h3
                className="text-lg font-semibold mb-4"
                style={{ color: colors.text }}
              >
                Are you sure you want to delete?
              </h3>

              <div className="flex justify-end gap-3">
                {/* NO BUTTON */}
                <button
                  className="px-6 py-2.5 rounded-lg font-semibold text-base transition-all duration-300"
                  style={{
                    backgroundColor: colors.border,
                    color: colors.text,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = `${colors.primary}1A`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = colors.border)
                  }
                  onClick={() => setShowDeleteConfirmation(false)}
                >
                  No
                </button>

                <button
                  className="px-6 py-2.5 rounded-lg text-white font-semibold text-base transition-all duration-300"
                  style={{
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "0 20px 25px -5px rgba(239,68,68,0.3)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.boxShadow = "none")
                  }
                  onClick={confirmDelete}
                >
                  Yes
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
